import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

import Paper from "@mui/material/Paper";

import moment from "moment";
import { useNavigate } from "react-router-dom";
import { TableFooter, Typography } from "@mui/material";

function TablePaginationActions(props) {
   const theme = useTheme();
   const { count, page, rowsPerPage, onPageChange } = props;

   const handleFirstPageButtonClick = (event) => {
      onPageChange(event, 0);
   };

   const handleBackButtonClick = (event) => {
      onPageChange(event, page - 1);
   };

   const handleNextButtonClick = (event) => {
      onPageChange(event, page + 1);
   };

   const handleLastPageButtonClick = (event) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
   };

   return (
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
         <IconButton
            onClick={handleFirstPageButtonClick}
            disabled={page === 0}
            aria-label="first page"
         >
            {theme?.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
         </IconButton>
         <IconButton
            onClick={handleBackButtonClick}
            disabled={page === 0}
            aria-label="previous page"
         >
            {theme?.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
         </IconButton>
         <IconButton
            onClick={handleNextButtonClick}
            disabled={page >= Math.ceil(count / rowsPerPage) - 1}
            aria-label="next page"
         >
            {theme?.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
         </IconButton>
         <IconButton
            onClick={handleLastPageButtonClick}
            disabled={page >= Math.ceil(count / rowsPerPage) - 1}
            aria-label="last page"
         >
            {theme?.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
         </IconButton>
      </Box>
   );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
   [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#007BFF",
      color: "#fff",
      fontWeight: 600,
      fontSize: 14,
   },
   [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      color: theme.palette.text.primary,
   },
}));

const StatusBadge = ({ status, type }) => {
   const getColors = () => {
      switch (status?.toLowerCase()) {
         case "open":
         case "approved":
            return { bg: "#e6f4ea", text: "#1e7e34" };
         case "closed":
         case "rejected":
            return { bg: "#fce8e6", text: "#d93025" };
         case "pending":
            return { bg: "#fef7e0", text: "#b06a00" };
         default:
            return { bg: "#f1f3f4", text: "#3c4043" };
      }
   };

   const colors = getColors();

   return (
      <Box
         sx={{
            backgroundColor: colors.bg,
            color: colors.text,
            px: 1.5,
            py: 0.5,
            borderRadius: "12px",
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "uppercase",
            display: "inline-block",
            textAlign: "center",
            minWidth: "80px",
            letterSpacing: "0.3px",
         }}
      >
         {status}
      </Box>
   );
};

const TripTable = ({ rows, limit = 0, page = 0, setPage, setLimit, listPagination }) => {
   const navigate = useNavigate();

   const handleChangePage = (event, newPage) => {
      setPage(newPage);
   };

   const handleChangeRowsPerPage = (event) => {
      setLimit(parseInt(event.target.value, 10));
      setPage(0);
   };

   // Avoid a layout jump when reaching the last page with empty rows.

   return (
      <Box sx={{ width: "100%" }}>
         <Paper
            elevation={0}
            sx={{
               width: "100%",
               mb: 2,
               border: "1px solid #dee2e6",
               borderRadius: "8px",
               overflow: "hidden",
            }}
         >
            <TableContainer>
               <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
                  <TableHead>
                     <TableRow>
                        <StyledTableCell>Vehicle Number</StyledTableCell>
                        <StyledTableCell>Driver Name</StyledTableCell>
                        <StyledTableCell>Created By</StyledTableCell>
                        <StyledTableCell>Customer Name</StyledTableCell>
                        <StyledTableCell>Start Date</StyledTableCell>
                        <StyledTableCell>Dispatch Date</StyledTableCell>
                        <StyledTableCell align="center">Trip Status</StyledTableCell>
                        <StyledTableCell align="center">Review Status</StyledTableCell>
                        <StyledTableCell align="right">Action</StyledTableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {rows.map((row) => {
                        return (
                           <TableRow
                              hover
                              key={row.tripid}
                              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                           >
                              <TableCell component="th" scope="row">
                                 <Typography variant="body2" fontWeight={600}>
                                    {row.vehicle_no}
                                 </Typography>
                              </TableCell>
                              <TableCell>
                                 <Typography variant="body2">{row.drivername}</Typography>
                              </TableCell>
                              <TableCell>
                                 <Typography variant="body2" color="text.secondary">
                                    {row.created_by_name}
                                 </Typography>
                              </TableCell>
                              <TableCell>
                                 <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                                    {row.customername}
                                 </Typography>
                              </TableCell>
                              <TableCell>
                                 <Typography variant="body2" color="text.secondary">
                                    {row?.trip_created_at
                                       ? moment(row?.trip_created_at).format(
                                            "DD MMM YYYY, hh:mm A"
                                         )
                                       : "-"}
                                 </Typography>
                              </TableCell>
                              <TableCell>
                                 <Typography variant="body2" color="text.secondary">
                                    {row.vehicle_disptch_date || "-"}
                                 </Typography>
                              </TableCell>
                              <TableCell align="center">
                                 <StatusBadge status={row?.trip_status} />
                              </TableCell>
                              <TableCell align="center">
                                 <StatusBadge status={row?.review_status} />
                              </TableCell>
                              <TableCell align="right">
                                 <button
                                    className="btn btn-outline-primary btn-sm px-3"
                                    onClick={() =>
                                       navigate("/trip/contractual/edit/" + row.tripid)
                                    }
                                 >
                                    View Details
                                 </button>
                              </TableCell>
                           </TableRow>
                        );
                     })}
                  </TableBody>
                  <TableFooter>
                     <TableRow>
                        <TablePagination
                           rowsPerPageOptions={[5, 10, 25]}
                           count={listPagination?.total || 0}
                           rowsPerPage={limit}
                           page={page}
                           onPageChange={handleChangePage}
                           onRowsPerPageChange={handleChangeRowsPerPage}
                           ActionsComponent={TablePaginationActions}
                           sx={{ borderBottom: "none" }}
                        />
                     </TableRow>
                  </TableFooter>
               </Table>
            </TableContainer>
         </Paper>
      </Box>
   );
};

export default TripTable;
